import type { Quote } from './types';

export type OrderConfirmationForm = {
  orderMode: string;
  rrzSales: string;
  customerName: string;
  customerPhone: string;
  customerManager: string;
  rrzOrderNumber: string;
  tenantName: string;
  tenantIdNumber: string;
  tenantCompanyName: string;
  tenantPhone: string;
  shippingAddress: string;
  rent: number | null;
  deposit: number | null;
  orderNote: string;
  debugType: string;
  externalComputerNumber: string;
  serviceGroupName: string;
  storeName: string;
  rrzRegistrationPhone: string;
  transferReason: string;
  shippingSpecialNeeds: string[];
  logisticsCompany: string;
  warehouse: string;
  orderType: string;
  paidDebugAmount: number | null;
  originalRrzOrderNumber: string;
  originalTenantName: string;
  originalTenantIdNumber: string;
  originalTenantCompanyName: string;
  liveCategory: string;
  orderAttribute: string;
  pushToWeCom: string;
};

export type OrderConfirmationField = keyof OrderConfirmationForm;
/** Labels shared by the actual confirmation form/detail and its field policy editor. */
export const ORDER_CONFIRMATION_FIELD_LABELS: Record<
  OrderConfirmationField | 'rentalCompany',
  string
> = {
  orderMode: '订单模式',
  rrzSales: '人人租销售',
  customerName: '客户名称',
  customerPhone: '客户电话',
  customerManager: '客户经理',
  rrzOrderNumber: '人人租订单编号',
  tenantName: '承租人',
  tenantIdNumber: '承租人身份证号',
  tenantCompanyName: '承租公司名称',
  tenantPhone: '承租人电话',
  shippingAddress: '收件地址',
  rent: '订单金额（租金/成交额）',
  deposit: '押金',
  orderNote: '订单备注',
  debugType: '调试类型',
  externalComputerNumber: '是否有外单电脑',
  serviceGroupName: '服务群名',
  storeName: '店铺名称',
  rrzRegistrationPhone: '人人租订单注册电话',
  transferReason: '调拨原因',
  shippingSpecialNeeds: '发货特殊需求备注',
  logisticsCompany: '物流公司',
  warehouse: '发货仓库',
  orderType: '订单类型',
  paidDebugAmount: '付费调试金额',
  originalRrzOrderNumber: '原人人租订单编号',
  originalTenantName: '原承租人',
  originalTenantIdNumber: '原承租人身份证号',
  originalTenantCompanyName: '原承租公司名称',
  liveCategory: '直播类目',
  orderAttribute: '订单属性',
  pushToWeCom: '推送企业微信',
  rentalCompany: '出租公司',
};
export type OrderConfirmationErrors = Partial<
  Record<OrderConfirmationField, string>
>;

/** Immutable fixed-domain snapshot created by the submit action. */
export interface OrderConfirmationSnapshot {
  schemaVersion: 1;
  confirmationId: string;
  quoteId: string;
  masterId: string;
  quoteVersion: number;
  status: 'submitted';
  submittedAt: string;
  fields: OrderConfirmationForm;
}

export function createOrderConfirmationSnapshot(
  quote: Quote,
  form: OrderConfirmationForm,
  submittedAt: string,
): OrderConfirmationSnapshot {
  return {
    schemaVersion: 1,
    confirmationId: `ORDER-CONFIRMATION:${quote.masterId ?? quote.quoteId}:V${quote.version}`,
    quoteId: quote.quoteId,
    masterId: quote.masterId ?? quote.quoteId,
    quoteVersion: quote.version,
    status: 'submitted',
    submittedAt,
    fields: structuredClone(form),
  };
}

export const ORDER_MODE_OPTIONS = [
  { value: 'trade', label: '商贸模式' },
  { value: 'rental', label: '租赁模式' },
  { value: 'service', label: '服务模式' },
] as const;

export const LIVE_CATEGORY_OPTIONS = [
  '服装',
  '珠宝饰品',
  '个人娱播',
  '超拟真',
  '鞋靴箱包',
  '美妆护肤',
  '礼品文创',
  '食品饮料',
  '鲜花绿植',
].map((label) => ({ value: label, label }));

export function liveCategoryOptionsWithDefaults(
  companyOptions?: readonly string[],
) {
  const values = new Set(LIVE_CATEGORY_OPTIONS.map(({ value }) => value));
  for (const option of companyOptions ?? []) {
    const value = option.trim();
    if (value) values.add(value);
  }
  return [...values].map((label) => ({ value: label, label }));
}

export const DEBUG_TYPE_OPTIONS = [
  { value: 'none', label: '无调试' },
  { value: 'remote-paid', label: '远程调试，付费' },
  { value: 'onsite-paid', label: '上门调试，付费' },
  { value: 'remote-free', label: '远程调试，免费' },
  { value: 'onsite-free', label: '上门调试，免费' },
] as const;

export const STORE_OPTIONS = [
  { value: '一店', label: '一店' },
  { value: '二店', label: '二店' },
  { value: '三店', label: '三店' },
  { value: '四店', label: '四店' },
  { value: '全案销售渠道', label: '全案销售渠道' },
  { value: '二手全案渠道', label: '二手全案渠道' },
] as const;

export const TRANSFER_REASON_OPTIONS = [
  '出租寄出',
  '销售寄出',
  '换货寄出',
  '出租+补寄',
  '原设备退回',
  '升级寄出',
  '换货+退回',
  '备用+退回',
  '转单发出',
  '备用寄出',
].map((label) => ({ value: label, label }));

export const SHIPPING_SPECIAL_NEED_OPTIONS = [
  '转订单无需发货',
  '等通知发货',
  '缺货',
  '重点设备带货上门',
  '客户自提',
  '货拉拉跟车上门',
  '同城闪送',
  '货拉拉',
].map((label) => ({ value: label, label }));

export const LOGISTICS_COMPANY_OPTIONS = [
  '顺丰云仓（顺丰快递）',
  'ZMKMKD',
  '京东大件（不预约）',
  '顺丰（带回单）',
  '丹鸟',
  '京广速递',
  '极兔',
  '极速',
  '安能物流',
  '长沙（顺丰快递）',
  '信丰',
  '百世汇通',
  '优速快递',
  '申通',
  '申通快递',
  '无需物流',
  'EMS',
  '顺丰快递（不预约）',
  '菜鸟速递',
  '顺丰云仓（闪送）',
  '顺丰云仓（货拉拉）',
  '顺丰（提醒配置回单口令）',
  '德邦',
  '韵达快递',
  '闪送',
  '已代发（勿重复发货，只要发回单）',
  '上门自带',
  '上门自提',
  '货拉拉',
  '京东小件（提醒配置回单口令）',
  '京东重货特快（提醒配置回单口令）',
  '顺丰快递（人人租顺丰卡航）',
  '顺丰快递（人人租顺丰标快）',
].map((label) => ({ value: label, label }));

export const WAREHOUSE_OPTIONS = [
  '租赁-顺丰-杭州仓',
  '恒和-顺丰-广州仓',
  '租赁-顺丰-广州仓',
  '恒和电脑云仓',
  '租赁-顺丰-长沙仓',
  '租赁-顺丰-厦门仓',
  '租赁-顺丰-成都仓',
  '租赁-顺丰-贵阳仓',
  '恒和电脑实物仓',
  '代发-正泰电工',
  '租赁-买断仓',
  '3C-总部品仓',
  '蜀租宝电脑实物仓',
  '蜀租宝电脑虚拟仓',
  '福建恒和电脑实物仓',
  '恒和电脑（委外虚拟仓）',
  '顺丰云仓-深圳',
  '3C-次品仓',
  '固定资产自用仓',
  '3C-代发仓',
  '租赁-实物管理仓',
].map((label) => ({
  value: label,
  label: label === '3C-总部品仓' ? '3C-总成品仓' : label,
}));

export const ORDER_CONFIRMATION_REQUIRED_FIELDS_BY_MODE = {
  trade: [
    'rrzOrderNumber',
    'shippingAddress',
    'customerName',
    'customerPhone',
    'customerManager',
    'tenantPhone',
    'rent',
    'transferReason',
    'logisticsCompany',
    'orderType',
    'liveCategory',
  ],
  rental: [
    'customerName',
    'customerPhone',
    'customerManager',
    'rrzOrderNumber',
    'tenantName',
    'tenantIdNumber',
    'tenantPhone',
    'shippingAddress',
    'rent',
    'debugType',
    'rrzRegistrationPhone',
    'transferReason',
    'logisticsCompany',
    'orderType',
    'liveCategory',
  ],
  service: [
    'customerName',
    'customerPhone',
    'customerManager',
    'tenantPhone',
    'rent',
    'transferReason',
    'logisticsCompany',
    'orderType',
    'liveCategory',
  ],
} as const satisfies Record<
  'trade' | 'rental' | 'service',
  readonly OrderConfirmationField[]
>;

/** Backwards-compatible alias for the rental confirmation matrix. */
export const ORDER_CONFIRMATION_REQUIRED_FIELDS =
  ORDER_CONFIRMATION_REQUIRED_FIELDS_BY_MODE.rental;

const MAX_LENGTHS: Partial<Record<OrderConfirmationField, number>> = {
  rrzSales: 200,
  customerName: 200,
  customerPhone: 64,
  customerManager: 200,
  rrzOrderNumber: 128,
  tenantName: 200,
  tenantIdNumber: 128,
  tenantCompanyName: 200,
  tenantPhone: 64,
  shippingAddress: 512,
  orderNote: 2000,
  debugType: 64,
  externalComputerNumber: 128,
  serviceGroupName: 128,
  storeName: 200,
  rrzRegistrationPhone: 64,
  transferReason: 200,
  logisticsCompany: 200,
  warehouse: 200,
  orderType: 64,
  liveCategory: 128,
  originalRrzOrderNumber: 128,
  originalTenantName: 200,
  originalTenantIdNumber: 128,
  originalTenantCompanyName: 200,
};

const ORDER_MODE_VALUES = new Set(
  ORDER_MODE_OPTIONS.map((option) => option.value),
);
const DEBUG_TYPE_VALUES = new Set(
  DEBUG_TYPE_OPTIONS.map((option) => option.value),
);
const TRANSFER_REASON_VALUES = new Set(
  TRANSFER_REASON_OPTIONS.map((option) => option.value),
);
const SHIPPING_SPECIAL_NEED_VALUES = new Set(
  SHIPPING_SPECIAL_NEED_OPTIONS.map((option) => option.value),
);
const LOGISTICS_COMPANY_VALUES = new Set(
  LOGISTICS_COMPANY_OPTIONS.map((option) => option.value),
);
const WAREHOUSE_VALUES = new Set(
  WAREHOUSE_OPTIONS.map((option) => option.value),
);

export function moneyCentsFromYuan(
  value: number | null,
  field: string,
): number | null {
  if (value === null) return null;
  if (!Number.isFinite(value) || value < 0)
    throw new Error(`确认下单字段 ${field} 不能映射为安全金额分`);
  const cents = value * 100;
  const rounded = Math.round(cents);
  if (
    !Number.isSafeInteger(rounded) ||
    Math.abs(cents - rounded) > Number.EPSILON * Math.max(1, Math.abs(cents))
  )
    throw new Error(`确认下单字段 ${field} 不能映射为安全金额分`);
  return rounded;
}

export function conditionalOrderFormPatch(
  mode: string,
  patch: Partial<OrderConfirmationForm>,
): Partial<OrderConfirmationForm> {
  const next = { ...patch };
  if (mode !== 'rental') {
    next.deposit = null;
  }
  return next;
}

function visibleMode(
  value: string,
): value is keyof typeof ORDER_CONFIRMATION_REQUIRED_FIELDS_BY_MODE {
  return ORDER_MODE_VALUES.has(
    value as (typeof ORDER_MODE_OPTIONS)[number]['value'],
  );
}

export const ORDER_CONFIRMATION_LOGISTICS_DEFAULTS = {
  logisticsCompany: '顺丰云仓（顺丰快递）',
  warehouse: '顺丰云仓-深圳',
} as const;

export function emptyOrderConfirmationForm(
  overrides: Partial<OrderConfirmationForm> = {},
): OrderConfirmationForm {
  return {
    orderMode: '',
    rrzSales: '',
    customerName: '',
    customerPhone: '',
    customerManager: '',
    rrzOrderNumber: '',
    tenantName: '',
    tenantIdNumber: '',
    tenantCompanyName: '',
    tenantPhone: '',
    shippingAddress: '',
    rent: null,
    deposit: null,
    orderNote: '',
    debugType: '',
    externalComputerNumber: '',
    serviceGroupName: '',
    storeName: '',
    rrzRegistrationPhone: '',
    transferReason: '',
    shippingSpecialNeeds: [],
    ...ORDER_CONFIRMATION_LOGISTICS_DEFAULTS,
    orderType: '零售业务',
    paidDebugAmount: null,
    originalRrzOrderNumber: '',
    originalTenantName: '',
    originalTenantIdNumber: '',
    originalTenantCompanyName: '',
    liveCategory: '',
    orderAttribute: '',
    pushToWeCom: '',
    ...overrides,
  };
}

export function orderModeForBusinessMode(
  businessModeCode: Quote['businessModeCode'],
): string {
  if (businessModeCode === 'rental' || businessModeCode === 'short_rental')
    return 'rental';
  if (businessModeCode === 'service' || businessModeCode === 'repair') {
    return 'service';
  }
  return 'trade';
}

export function orderConfirmationFormForQuote(
  quote: Quote,
): OrderConfirmationForm {
  const previous = quote.orderConfirmation?.fields;
  return emptyOrderConfirmationForm({
    ...previous,
    orderMode:
      previous?.orderMode || orderModeForBusinessMode(quote.businessModeCode),
    customerName: previous?.customerName || quote.customer.name,
    customerPhone: previous?.customerPhone || quote.customer.phone,
    // 只恢复已手填的下单字段，不从报价总额或报价押金推导。
    rent: previous?.rent ?? null,
    deposit: previous?.deposit ?? null,
  });
}

export function validateOrderConfirmationForm(
  form: OrderConfirmationForm,
): OrderConfirmationErrors {
  const errors: OrderConfirmationErrors = {};
  if (!visibleMode(form.orderMode)) {
    errors.orderMode = '请选择订单模式';
    return errors;
  }
  const dealAmountLabel =
    form.orderMode === 'rental' ? '租金（订单总额）' : '成交额';

  const labels = ORDER_CONFIRMATION_FIELD_LABELS;
  for (const field of ORDER_CONFIRMATION_REQUIRED_FIELDS_BY_MODE[
    form.orderMode
  ]) {
    if (
      field === 'rent' ? form.rent === null : !String(form[field] ?? '').trim()
    )
      errors[field] =
        field === 'rent'
          ? `请填写有效的${dealAmountLabel}`
          : `请填写${labels[field] ?? field}`;
  }

  try {
    moneyCentsFromYuan(form.rent, 'rent');
  } catch {
    errors.rent = `请填写有效的${dealAmountLabel}`;
  }
  try {
    moneyCentsFromYuan(form.deposit, 'deposit');
  } catch {
    errors.deposit = '请填写有效的押金';
  }
  try {
    moneyCentsFromYuan(form.paidDebugAmount, 'paidDebugAmount');
  } catch {
    errors.paidDebugAmount = '请填写有效的付费调试金额';
  }

  if (
    form.debugType &&
    !DEBUG_TYPE_VALUES.has(
      form.debugType as (typeof DEBUG_TYPE_OPTIONS)[number]['value'],
    )
  )
    errors.debugType = '请选择有效的调试类型';
  if (form.orderMode !== 'rental' && form.deposit !== null)
    errors.deposit = '非租赁模式不能填写押金';
  if (
    form.debugType !== 'remote-paid' &&
    form.debugType !== 'onsite-paid' &&
    form.paidDebugAmount !== null
  )
    errors.paidDebugAmount = '仅付费调试类型可填写调试金额';
  if (!TRANSFER_REASON_VALUES.has(form.transferReason))
    errors.transferReason = '请选择有效的调拨原因';
  if (!LOGISTICS_COMPANY_VALUES.has(form.logisticsCompany))
    errors.logisticsCompany = '请选择有效的物流公司';
  if (form.warehouse && !WAREHOUSE_VALUES.has(form.warehouse))
    errors.warehouse = '请选择有效的发货仓库';
  if (
    form.shippingSpecialNeeds.length > 1 ||
    new Set(form.shippingSpecialNeeds).size !==
      form.shippingSpecialNeeds.length ||
    form.shippingSpecialNeeds.some(
      (value) => !SHIPPING_SPECIAL_NEED_VALUES.has(value),
    )
  )
    errors.shippingSpecialNeeds = '发货特殊需求只能单选一个批准选项';
  if (form.orderType !== '零售业务')
    errors.orderType = '订单类型固定为零售业务';
  if (form.transferReason === '转单发出') {
    for (const [field, label] of [
      ['originalRrzOrderNumber', '原人人租订单编号'],
      ['originalTenantName', '原承租人'],
      ['originalTenantIdNumber', '原承租人身份证号'],
    ] as const)
      if (!form[field].trim()) errors[field] = `请填写${label}`;
  } else if (
    form.originalRrzOrderNumber ||
    form.originalTenantName ||
    form.originalTenantIdNumber ||
    form.originalTenantCompanyName
  ) {
    errors.originalRrzOrderNumber = '仅转单发出可填写原订单字段';
  }
  if (
    form.rent === 0 &&
    !['出租+补寄', '换货寄出', '备用寄出'].includes(form.transferReason)
  )
    errors.rent = '当前调拨原因不允许零金额';
  for (const [field, maxLength] of Object.entries(MAX_LENGTHS)) {
    const value = form[field as OrderConfirmationField];
    if (typeof value === 'string' && value.length > maxLength)
      errors[field as OrderConfirmationField] =
        `${labels[field as OrderConfirmationField] ?? field}不能超过${maxLength}个字符`;
  }
  return errors;
}
